/**
 * useCredits Hook - Credit system management for SYLink Pentest
 * Provides credit balance, warnings, and consumption utilities
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import api, { auth } from '../api/client';

// Credit cost constants (should match backend)
export const CREDIT_COSTS = {
  // Scan credits
  scan_external_basic: 10,
  scan_external_advanced: 20,
  scan_internal_basic: 15,
  scan_internal_advanced: 30,
  scan_webapp_basic: 12,
  scan_webapp_advanced: 25,
  scan_api_basic: 10,
  scan_api_advanced: 20,
  scan_ot_basic: 15,
  scan_ot_complete: 50,
  scan_iot_basic: 12,
  scan_iot_complete: 35,
  scan_ad_recon: 20,
  scan_ad_full: 40,

  // Leak detection
  leak_detection: 15,

  // Reports
  report_executive: 5,
  report_technical: 8,

  // Audit
  audit_compliance: 25
};

// Warning thresholds
const WARNING_THRESHOLD_LOW = 50;
const WARNING_THRESHOLD_CRITICAL = 10;

/**
 * Main credits hook
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoRefresh - Enable auto-refresh of credit data
 * @param {number} options.refreshInterval - Refresh interval in ms (default: 60000)
 */
export function useCredits(options = {}) {
  const { autoRefresh = true, refreshInterval = 60000 } = options;

  const [summary, setSummary] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const refreshIntervalRef = useRef(null);

  // Fetch credit summary
  const fetchSummary = useCallback(async () => {
    // Skip if not authenticated or token is expired
    if (!auth.isAuthenticated() || !auth.isTokenValidClientSide()) {
      return null;
    }
    try {
      const response = await api.get('/credits/summary');
      setSummary(response.data);
      return response.data;
    } catch (err) {
      // Silently handle 400/401 errors (user without company or not authenticated) - this is expected
      if (err.response?.status === 400 || err.response?.status === 401) {
        return null;
      }
      console.error('Failed to fetch credit summary:', err);
      setError(err.response?.data?.detail || 'Failed to fetch credits');
      return null;
    }
  }, []);

  // Fetch warnings
  const fetchWarnings = useCallback(async () => {
    // Skip if not authenticated or token is expired
    if (!auth.isAuthenticated() || !auth.isTokenValidClientSide()) {
      return [];
    }
    try {
      const response = await api.get('/credits/warnings');
      setWarnings(response.data.warnings || []);
      return response.data.warnings;
    } catch (err) {
      // Silently handle 400/401 errors (user without company or not authenticated)
      if (err.response?.status !== 400 && err.response?.status !== 401) {
        console.error('Failed to fetch credit warnings:', err);
      }
      return [];
    }
  }, []);

  // Fetch balances
  const fetchBalances = useCallback(async () => {
    // Skip if not authenticated or token is expired
    if (!auth.isAuthenticated() || !auth.isTokenValidClientSide()) {
      return [];
    }
    try {
      const response = await api.get('/credits/balances');
      setBalances(response.data);
      return response.data;
    } catch (err) {
      // Silently handle 400/401 errors (user without company or not authenticated)
      if (err.response?.status !== 400 && err.response?.status !== 401) {
        console.error('Failed to fetch credit balances:', err);
      }
      return [];
    }
  }, []);

  // Refresh all credit data
  const refresh = useCallback(async () => {
    // Skip if not authenticated or token is expired
    if (!auth.isAuthenticated() || !auth.isTokenValidClientSide()) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    try {
      await Promise.all([
        fetchSummary(),
        fetchWarnings(),
        fetchBalances()
      ]);
    } catch (err) {
      setError('Failed to refresh credit data');
    } finally {
      setLoading(false);
    }
  }, [fetchSummary, fetchWarnings, fetchBalances]);

  // Check if action can be performed
  const canConsume = useCallback(async (actionType) => {
    try {
      const response = await api.get(`/credits/can-consume/${actionType}`);
      return response.data;
    } catch (err) {
      console.error('Failed to check credit consumption:', err);
      return { can_consume: false, message: 'Failed to check credits' };
    }
  }, []);

  // Consume credits for an action
  const consumeCredits = useCallback(async (actionType, workspaceId = null, description = null) => {
    try {
      const response = await api.post('/credits/consume', {
        action_type: actionType,
        workspace_id: workspaceId,
        description: description
      });

      // Refresh summary after consumption
      await fetchSummary();

      return { success: true, data: response.data };
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to consume credits';
      return { success: false, error: errorMessage };
    }
  }, [fetchSummary]);

  // Get credit cost for action type
  const getCreditCost = useCallback((actionType) => {
    return CREDIT_COSTS[actionType] || 0;
  }, []);

  // Check if credits are low
  const isLow = useCallback(() => {
    if (!summary) return false;
    return summary.total_available <= WARNING_THRESHOLD_LOW;
  }, [summary]);

  // Check if credits are critical
  const isCritical = useCallback(() => {
    if (!summary) return false;
    return summary.total_available <= WARNING_THRESHOLD_CRITICAL;
  }, [summary]);

  // Check if credits are exhausted
  const isExhausted = useCallback(() => {
    if (!summary) return false;
    return summary.total_available <= 0;
  }, [summary]);

  // Initial load
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Auto-refresh setup
  useEffect(() => {
    if (autoRefresh && refreshInterval > 0) {
      refreshIntervalRef.current = setInterval(refresh, refreshInterval);

      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    }
  }, [autoRefresh, refreshInterval, refresh]);

  return {
    // State
    summary,
    warnings,
    balances,
    loading,
    error,

    // Computed
    totalAvailable: summary?.total_available || 0,
    monthlyCredits: summary?.monthly_credits || 0,
    packCredits: summary?.pack_credits || 0,
    hasCredits: (summary?.total_available || 0) > 0,
    isLow: isLow(),
    isCritical: isCritical(),
    isExhausted: isExhausted(),

    // Actions
    refresh,
    canConsume,
    consumeCredits,
    getCreditCost,

    // Helpers
    CREDIT_COSTS
  };
}

/**
 * Hook for fetching public credit plans
 */
export function useCreditPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await api.get('/credits/plans');
        setPlans(response.data);
      } catch (err) {
        // Silently handle 401 errors (authentication issues)
        if (err.response?.status !== 401) {
          setError(err.response?.data?.detail || 'Failed to fetch plans');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  return { plans, loading, error };
}

/**
 * Hook for fetching credit packs
 */
export function useCreditPacks() {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPacks = async () => {
      try {
        const response = await api.get('/credits/packs');
        setPacks(response.data);
      } catch (err) {
        // Silently handle 401 errors (authentication issues)
        if (err.response?.status !== 401) {
          setError(err.response?.data?.detail || 'Failed to fetch packs');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPacks();
  }, []);

  return { packs, loading, error };
}

/**
 * Hook for fetching credit costs
 */
export function useCreditCosts() {
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCosts = async () => {
      try {
        const response = await api.get('/credits/costs');
        setCosts(response.data);
      } catch (err) {
        // Silently handle 401 errors (authentication issues)
        if (err.response?.status !== 401) {
          setError(err.response?.data?.detail || 'Failed to fetch costs');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCosts();
  }, []);

  // Get cost by action type
  const getCostByAction = useCallback((actionType) => {
    return costs.find(c => c.action_type === actionType);
  }, [costs]);

  // Get costs by category
  const getCostsByCategory = useCallback((category) => {
    return costs.filter(c => c.category === category);
  }, [costs]);

  return { costs, loading, error, getCostByAction, getCostsByCategory };
}

/**
 * Hook for credit transactions history
 */
export function useCreditTransactions(options = {}) {
  const { limit = 50, actionType = null, autoRefresh = false } = options;

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTransactions = useCallback(async () => {
    // Skip if not authenticated or token is expired
    if (!auth.isAuthenticated() || !auth.isTokenValidClientSide()) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: limit.toString() });
      if (actionType) {
        params.append('action_type', actionType);
      }

      const response = await api.get(`/credits/transactions?${params}`);
      setTransactions(response.data);
    } catch (err) {
      // Silently handle 400/401 errors (user without company or not authenticated)
      if (err.response?.status !== 400 && err.response?.status !== 401) {
        setError(err.response?.data?.detail || 'Failed to fetch transactions');
      }
    } finally {
      setLoading(false);
    }
  }, [limit, actionType]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  return { transactions, loading, error, refresh: fetchTransactions };
}

export default useCredits;
